import React, { Suspense, useEffect, useMemo } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async'; // <--- BỔ SUNG QUAN TRỌNG
import { Box, CircularProgress, CssBaseline } from '@mui/material';

// Components
import AppRoutes from './routes';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ContactBubble from './components/layout/ContactBubble/ContactBubble';
import CookieConsent from './components/CookieConsent'; // Đường dẫn tới file bạn vừa tạo

// Config
import './i18n'; 

// 1. ScrollToTop Component: Cuộn lên đầu trang khi chuyển route
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  return null;
};

function App() {
  const { i18n } = useTranslation();

  // 2. Tối ưu hóa Theme: Sử dụng useMemo để Re-render theme khi ngôn ngữ thay đổi
  // Giúp MUI tự động đảo chiều layout nếu bạn có ngôn ngữ RTL (như tiếng Ả Rập) sau này
  const theme = useMemo(() => createTheme({
    palette: {
      primary: {
        main: '#FFCC00',
        light: '#FFDE59',
        dark: '#D4A600',
        contrastText: '#000',
      },
      secondary: {
        main: '#9c27b0',
        light: '#ba68c8',
        dark: '#7b1fa2',
        contrastText: '#fff',
      },
      custom: {
        logoRed: '#D40511',
      },
    },
    // Tự động set hướng dựa trên ngôn ngữ hiện tại
    direction: i18n.dir(), 
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
  }), [i18n.language]); // Dependency: Re-create theme khi language thay đổi

  // 3. Effect cập nhật attributes cho thẻ HTML (cần thiết cho CSS thuần)
  useEffect(() => {
    document.documentElement.setAttribute('dir', i18n.dir());
    document.documentElement.lang = i18n.language;
  }, [i18n, i18n.language]);

  return (
    // HelmetProvider cần thiết để quản lý thẻ <head> (Title, Meta SEO) từ các trang con
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline /> {/* Reset CSS */}
        
        <BrowserRouter>
          <ScrollToTop /> {/* Đặt ở đây để hoạt động với Router */}
          
          <Suspense
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100vh',
                  bgcolor: 'background.default'
                }}
              >
                <CircularProgress color="primary" />
              </Box>
            }
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              
              <Header />
              
              <Box
                component="main"
                sx={{
                  flexGrow: 1,
                  // Lưu ý: Nếu Header của bạn là position="fixed", bạn cần tăng padding-top
                  // bằng chiều cao của Header (ví dụ: 64px hoặc theme.mixins.toolbar)
                  paddingTop: { xs: '56px', sm: '64px' }, 
                  position: 'relative',
                  bgcolor: '#f4f6f8' // Màu nền nhẹ cho toàn web để nội dung nổi bật
                }}
              >
                <AppRoutes />
              </Box>

              <Footer />
              
              <ContactBubble />
              
              {/* <CookieConsent /> */}
            </Box>
          </Suspense>
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;